import i18n from '../../plugins/i18n';
import {
  changedByObject,
  mapProductName,
} from '../../helpers/transformToPresentableData';
import { formatCommissioningContactPerson } from '../../helpers/commissioningContactPerson';

// Maps the (upcoming) `journey_type` values to the legacy `journey` values
// still used for display/filtering in the list view (e.g. 'water', 'gas').
const JOURNEY_TYPE_TO_JOURNEY = {
  ELECTRIC_METER_ORDER: 'electricity',
  GAS_METER_ORDER: 'gas',
  SITE_ELECTRIC_METER_ORDER: 'electricity',
  WATER_METER_ORDER: 'water',
};

function resolveJourneyType(order = {}) {
  const journeyType = order.journey_type || order.payload?.journey_type;

  return typeof journeyType === 'string'
    ? journeyType.trim().toUpperCase()
    : '';
}

export function isSealingOrder(order = {}) {
  return resolveJourneyType(order) === 'SEALING_ELECTRICITY_ORDER';
}

export function isEventElectricityOrder(order = {}) {
  return resolveJourneyType(order) === 'EVENT_ELECTRIC_METER_ORDER';
}

function resolveJourney(order) {
  const journeyType = resolveJourneyType(order);

  return JOURNEY_TYPE_TO_JOURNEY[journeyType] || order.journey;
}

export function tableOrderMapper(order) {
  const journeyType = resolveJourneyType(order);

  return {
    orderId: order.id,
    date: new Date(order.request_date),
    applicant: applicantObject(order.applicant),
    product:
      order.products || journeyType === 'SITE_ELECTRIC_METER_ORDER'
        ? productObject(order, journeyType)
        : null,
    address: installationAddressObject(order),
    lastChangedBy: changedByObject(order),
    regional_team: order.regional_team ? order.regional_team.team_name : '',
    status: order.status,
    journey: resolveJourney(order),
  };
}

function applicantObject(applicant = {}) {
  return formatCommissioningContactPerson(applicant);
}

function productObject(order, journeyType) {
  const productName =
    journeyType === 'SITE_ELECTRIC_METER_ORDER'
      ? i18n.t('clerk_orders_overview_table.construction_power')
      : mapProductName(order.products);

  return (
    productName +
    (order.own_contribution_selected === true
      ? `, ${i18n.t('clerk_orders_overview_table.with_own_contribution')}`
      : '') +
    (order.preferred_network_connection === true
      ? `, ${i18n.t(
          'clerk_orders_overview_table.with_preferred_network_connection'
        )}`
      : '')
  );
}

function hasPlotData(address) {
  return Boolean(
    address &&
      (address.district ||
        address.landing ||
        address.plot_number ||
        address.plot)
  );
}

export function needsSiteElectricityAddressDetails(order = {}) {
  if (resolveJourneyType(order) !== 'SITE_ELECTRIC_METER_ORDER') {
    return false;
  }

  return !hasPlotData(
    order.payload?.connection_address_locator ||
      order.connection_address_locator ||
      order.product_installation_with_plot_number_address
  );
}

export function mergeOrderAddressDetails(order, details) {
  if (!details) {
    return order;
  }

  return {
    ...order,
    payload: details.payload || order.payload,
    connection_address_locator:
      details.connection_address_locator || order.connection_address_locator,
    product_installation_full_address:
      details.product_installation_full_address ||
      order.product_installation_full_address,
    product_installation_with_plot_number_address:
      details.product_installation_with_plot_number_address ||
      order.product_installation_with_plot_number_address,
  };
}

function formatAddress(address = {}) {
  const street = [address.street, address.street_number]
    .filter(Boolean)
    .join(' ');
  const city = [address.zip, address.city].filter(Boolean).join(' ');

  return [street, city].filter(Boolean).join(', ') || '-';
}

function getConnectionAddress(order, fullAddress) {
  const payload = order.payload || {};
  const locator =
    payload.connection_address_locator || order.connection_address_locator;
  const locatorMode = locator?.address_mode || locator?.addressMode;

  if (locatorMode === 'plot') {
    return {
      ...(fullAddress || {}),
      ...(payload.connection_address || {}),
      ...locator,
    };
  }

  return payload.connection_address || locator || fullAddress;
}

function formatPlotAddress(address = {}) {
  const street = [address.street, address.street_number]
    .filter(Boolean)
    .join(' ');

  return [
    street,
    address.district ? `Gemarkung: ${address.district}` : null,
    address.landing ? `Flur: ${address.landing}` : null,
    address.plot_number || address.plot
      ? `Flurstück: ${address.plot_number || address.plot}`
      : null,
    address.zip,
    address.city || address.location,
  ]
    .filter(Boolean)
    .join(', ');
}

function installationAddressObject(order) {
  const fullAddress = order.product_installation_full_address;
  const legacyPlotAddress = order.product_installation_with_plot_number_address;
  const connectionAddress = getConnectionAddress(order, fullAddress);
  const locator =
    order.payload?.connection_address_locator ||
    order.connection_address_locator;
  const locatorMode = locator?.address_mode || locator?.addressMode;

  if (locatorMode === 'plot') {
    return formatPlotAddress(connectionAddress);
  }

  if (legacyPlotAddress) {
    return [
      legacyPlotAddress.new_building_area_name,
      formatPlotAddress({
        ...legacyPlotAddress,
        street: legacyPlotAddress.street || fullAddress?.street,
        street_number:
          legacyPlotAddress.street_number || fullAddress?.street_number,
      }),
    ]
      .filter(Boolean)
      .join(', ');
  }

  return formatAddress(connectionAddress);
}
